import { useLiveQuery } from "dexie-react-hooks";
import {
	type Budget,
	type Category,
	db,
	type IncomeStream,
	type Transaction,
	type Wallet,
} from "@/lib/db";
import { type Allowance, computeAllowance, monthOf } from "@/lib/ledger";

// Live finance views over Dexie; they update as soon as a write or a pull lands.

const live = <T extends { deleted_at: string | null }>(r: T) => !r.deleted_at;
const byName = <T extends { name: string; archived: boolean }>(a: T, b: T) =>
	Number(a.archived) - Number(b.archived) || a.name.localeCompare(b.name);

export function useWallets(): Wallet[] | undefined {
	return useLiveQuery(async () => (await db.wallets.filter(live).toArray()).sort(byName), []);
}

export function useCategories(): Category[] | undefined {
	return useLiveQuery(async () => (await db.categories.filter(live).toArray()).sort(byName), []);
}

export function useStreams(): IncomeStream[] | undefined {
	return useLiveQuery(
		async () => (await db.income_streams.filter(live).toArray()).sort(byName),
		[],
	);
}

/** Every live transaction, newest first (a personal ledger stays small). */
export function useTransactions(): Transaction[] | undefined {
	return useLiveQuery(
		async () => (await db.transactions.orderBy("occurred_at").reverse().toArray()).filter(live),
		[],
	);
}

export function useBudgets(month: string): Budget[] | undefined {
	return useLiveQuery(
		async () => (await db.budgets.where("month").equals(month).toArray()).filter(live),
		[month],
	);
}

/** Today's allowance from local rows (works offline; D42). */
export function useAllowance(day: string): Allowance | undefined {
	return useLiveQuery(async () => {
		const [budgets, categories, transactions] = await Promise.all([
			db.budgets.where("month").equals(monthOf(day)).toArray(),
			db.categories.toArray(),
			db.transactions.toArray(),
		]);
		return computeAllowance(day, budgets, categories, transactions);
	}, [day]);
}
